import { Table } from 'antd'
import { ColumnsType } from 'antd/es/table/interface'
import React, { FunctionComponent, memo, useMemo } from 'react'

import { payoutPriceRenderer } from '../../utils/buybackPayoutHelpersNew'
import { Money } from '../Money/Money'

export interface BuybackPayoutDebtData {
  key: string
  name: string
  amount: number
  fee: number
  secured: boolean
}

export interface BuybackPayoutProcessCostData {
  key: string
  name: string
  amount: number
}

export interface BuybackFormPayoutOutcomeTableProps {
  debts: BuybackPayoutDebtData[]
  processCosts: BuybackPayoutProcessCostData[]
  commission?: number
  baseCommission: number
  debtFeeTotal?: number
  baseDebtFeeTotal: number
  debtAmountTotal: number
  processCostTotal: number
}

interface ExpandableRowChild {
  key: string
  category: string
  description: string
  fee: number | string
  amount: number
  suma: number
}

interface ExpandableRowData {
  key: string
  category: string
  description: string | React.ReactNode
  baseAmount: number
  fee: number
  amount: number
  children?: ExpandableRowChild[]
  suma?: number
}

const commissionRenderer = (record: ExpandableRowData) => {
  return (
    <span>
      {!!record.baseAmount && record.baseAmount !== record.fee && (
        <Money
          amount={record.baseAmount}
          decimalNumbers={0}
          className="line-through decoration-black text-gray-400 mr-2"
        />
      )}
      <span>{payoutPriceRenderer(record.fee)}</span>
    </span>
  )
}

const columns: ColumnsType<ExpandableRowData> = [
  {
    title: 'Položka',
    dataIndex: 'category',
    key: 'category',
    width: '30%',
    className: 'group-hover:text-primaryHover',
  },
  {
    title: 'Popis',
    dataIndex: 'description',
    key: 'description',
  },
  {
    title: 'Částka',
    dataIndex: 'amount',
    key: 'amount',
    className: '!text-right',
    render: (_: number, record: ExpandableRowData) => {
      if (record.key === 'commission')
        return (
          <span>
            {!!record.baseAmount && record.baseAmount !== record.fee && (
              <Money
                amount={record.baseAmount}
                decimalNumbers={0}
                className="line-through decoration-black text-gray-400 mr-2"
                symbol="Kč"
              />
            )}
          </span>
        )
      if (record.key.startsWith('debt'))
        return (
          <div>
            {payoutPriceRenderer(record.amount)}
            <span className="px-2">+</span>
            {commissionRenderer(record)}
            <span className="pl-2">=</span>
          </div>
        )
      return null
    },
  },
  {
    title: 'Součet',
    dataIndex: 'suma',
    key: 'suma',
    width: 100,
    className: '!text-right whitespace-nowrap border-r border-gray-300',
    render: payoutPriceRenderer,
  },
]

const expandable = {
  expandedRowClassName: '!text-gray-400',
  expandRowByClick: true,
}

const rowClassName = (record: ExpandableRowData) =>
  record.children?.length
    ? 'group cursor-pointer bg-[#fafafa] transition-colors text-gray-500 hover:bg-gray-50'
    : 'bg-[#fafafa] text-gray-500'

export const BuybackFormPayoutOutcomeTable: FunctionComponent<BuybackFormPayoutOutcomeTableProps> =
  memo(
    ({
      debts,
      processCosts,
      commission,
      baseCommission,
      debtFeeTotal,
      debtAmountTotal,
      processCostTotal,
      baseDebtFeeTotal,
    }: BuybackFormPayoutOutcomeTableProps) => {
      const outcomeSource = useMemo<ExpandableRowData[]>(() => {
        const currentDebtFeeTotal =
          typeof debtFeeTotal === 'number' ? debtFeeTotal : baseDebtFeeTotal
        const currentCommission =
          typeof commission === 'number' ? commission : baseCommission

        const rows: ExpandableRowData[] = []

        if (debts.length) {
          rows.push({
            key: 'debts',
            category: `Závazky (${debts.length})`,
            description: 'Výše závazku + odměna vyřízení',
            baseAmount: baseDebtFeeTotal,
            fee: currentDebtFeeTotal,
            amount: debtAmountTotal,
            suma: currentDebtFeeTotal + debtAmountTotal,
            children: debts.map((debt, index) => ({
              key: `debt-${debt.key}${index}`,
              category: debt.name,
              description: debt.secured ? 'Se zajištěním' : 'Nezajištěno',
              fee: debt.fee,
              amount: debt.amount,
              suma: debt.fee + debt.amount,
            })),
          })
        }

        if (processCosts.length) {
          rows.push({
            key: 'fees',
            category: `Poplatky (${processCosts.length})`,
            description: 'Administrativní poplatky',
            fee: 0,
            baseAmount: 0,
            amount: processCostTotal,
            suma: processCostTotal,
            children: processCosts.map(cost => ({
              key: `cost-${cost.key}`,
              category: cost.name,
              description: '---',
              fee: 0,
              amount: cost.amount,
              suma: cost.amount,
            })),
          })
        }

        rows.push({
          key: 'commission',
          category: 'Provize',
          description: 'Odměna za zprostředkování',
          baseAmount: baseCommission,
          fee: currentCommission,
          amount: 0,
          suma: currentCommission,
        })

        return rows
      }, [
        debtFeeTotal,
        baseDebtFeeTotal,
        commission,
        baseCommission,
        debts,
        processCosts,
        debtAmountTotal,
        processCostTotal,
      ])

      return (
        <Table<ExpandableRowData>
          size="small"
          pagination={false}
          dataSource={outcomeSource}
          columns={columns}
          expandable={expandable}
          showHeader={false}
          rowClassName={rowClassName}
        />
      )
    }
  )
